import { useState, useEffect } from 'react';
import { calculateAuraStats, getAuraBadge } from '../utils/aura';

function SpotlightFeed({ onNavigate }) {
  const [spotlightedAvatars, setSpotlightedAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSpotlightedAvatars();

    // Refresh every 10 seconds to update time remaining
    const interval = setInterval(loadSpotlightedAvatars, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadSpotlightedAvatars = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/spotlight`);
      if (response.ok) {
        const data = await response.json();
        setSpotlightedAvatars(data);
      } else {
        setError('Failed to load spotlighted avatars');
      }
    } catch (err) {
      setError('Failed to load spotlighted avatars');
      console.error('Load spotlighted avatars error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeRemaining = (milliseconds) => {
    if (milliseconds <= 0) return 'Expired';

    const totalMinutes = Math.floor(milliseconds / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const handleAvatarClick = (username) => {
    // Navigate to user profile
    if (onNavigate) {
      onNavigate('profile', username);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner large"></div>
        <p>Loading spotlight feed...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-text">{error}</p>
        <button onClick={loadSpotlightedAvatars} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="spotlight-feed-container">
      <div className="spotlight-header">
        <h1 className="spotlight-title">⭐ Spotlight Feed ⭐</h1>
        <p className="spotlight-subtitle">
          Featured avatars sorted by time remaining
        </p>
        <p className="spotlight-info">
          Click on any avatar to visit their profile
        </p>
      </div>

      {spotlightedAvatars.length === 0 ? (
        <div className="empty-spotlight">
          <div className="empty-icon">⭐</div>
          <h2>No Active Spotlights</h2>
          <p>Be the first to activate a Spotlight boost!</p>
          <button
            className="go-to-boosts-btn"
            onClick={() => onNavigate && onNavigate('boosts')}
          >
            Go to Power-Ups
          </button>
        </div>
      ) : (
        <div className="spotlight-grid">
          {spotlightedAvatars.map((avatar, index) => {
            // Calculate Aura stats for badge display
            const auraStats = calculateAuraStats(
              avatar.auraStats?.fireSwipes || 0,
              avatar.auraStats?.leftSwipes || 0
            );
            const auraBadge = getAuraBadge(auraStats.auraPercent, auraStats.totalSwipes);

            return (
              <div
                key={avatar.id}
                className="spotlight-card"
                onClick={() => handleAvatarClick(avatar.username)}
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                {/* Time Remaining Badge */}
                <div className="spotlight-time-remaining">
                  ⏱️ {formatTimeRemaining(avatar.spotlightTimeRemaining)}
                </div>

                <div className="spotlight-image-container">
                  <img
                    src={avatar.avatarUrl}
                    alt={`${avatar.username}'s avatar`}
                    className="spotlight-image"
                  />

                  {/* Spotlight Glow Effect */}
                  <div className="spotlight-glow"></div>

                  {/* Aura Badge Overlay */}
                  <div className="aura-badge-overlay">
                    <img src={auraBadge.emoji} alt={auraBadge.label} className="aura-badge-image" />
                  </div>
                </div>

                <div className="spotlight-info-section">
                  <h3 className="spotlight-username">{avatar.username}</h3>

                  {auraStats.totalSwipes > 0 && (
                    <p className="spotlight-aura">
                      {auraStats.auraPercent}% Aura • {auraStats.totalSwipes} swipes
                    </p>
                  )}

                  <div className="spotlight-stats">
                    <div className="spotlight-stat">
                      <span className="stat-label">🔥 Fire</span>
                      <span className="stat-value">{auraStats.fireSwipes}</span>
                    </div>
                    <div className="spotlight-stat">
                      <span className="stat-label">💀 L's</span>
                      <span className="stat-value">{auraStats.leftSwipes}</span>
                    </div>
                  </div>

                  <div className="click-hint">
                    👆 Click to view profile
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SpotlightFeed;
